import React, { useEffect, useState } from 'react';
import { QrCode, MessageCircle } from 'lucide-react';

/**
 * QR code that, when scanned by a phone camera, opens WhatsApp deep-linked
 * to our business number with a pre-filled message. Renders the QR using
 * Google Chart API as a zero-dependency fallback; if `qrcode` is installed
 * locally we use that instead.
 *
 * Click on desktop opens WhatsApp Web.
 */
export default function QRCodeStart({
  whatsappNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '+1XXXXXXXXXX',
  prefillMessage = 'Hi, I want to get started with Commerce Index',
  size = 180,
  label = 'Scan to start on WhatsApp',
  className = '',
}) {
  // Build wa.me link
  const waLink = `https://wa.me/${whatsappNumber.replace(/[^\d]/g, '')}?text=${encodeURIComponent(prefillMessage)}`;
  const [qrSrc, setQrSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Prefer local `qrcode` library if present; else use the qrserver fallback.
    (async () => {
      try {
        const mod = await import(/* webpackChunkName: "qrcode" */ 'qrcode');
        const url = await mod.toDataURL(waLink, { width: size * 2, margin: 1, color: { dark: '#FFFFFF', light: '#0D162800' } });
        if (!cancelled) setQrSrc(url);
      } catch {
        // Fallback: use a public QR generator (no PII in the URL — just our wa.me link)
        const encoded = encodeURIComponent(waLink);
        if (!cancelled) setQrSrc(`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encoded}&bgcolor=0d1628&color=ffffff`);
      }
    })();
    return () => { cancelled = true; };
  }, [waLink, size]);

  const trackClick = () => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'qr_code_whatsapp_click' });
    }
  };

  return (
    <a
      href={waLink}
      target="_blank"
      rel="noopener noreferrer"
      onClick={trackClick}
      className={`inline-flex flex-col items-center bg-[#0D1628] border border-[#22304A] rounded-xl p-4 hover:border-[#3EF1C7] transition ${className}`}
      title="Scan with your phone camera to start on WhatsApp"
    >
      <div className="bg-[#0D1628] rounded p-1" style={{ width: size, height: size }}>
        {qrSrc ? (
          <img
            src={qrSrc}
            alt="WhatsApp QR — scan with your phone camera"
            width={size}
            height={size}
            className="block"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#64748B]">
            <QrCode className="w-12 h-12" />
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-[#94A3B8]">
        <MessageCircle className="w-3 h-3 text-[#25D366]" />
        <span>{label}</span>
      </div>
    </a>
  );
}
